/*
 * Copyright ish group pty ltd. All rights reserved. https://www.ish.com.au
 * No copying or use of this code is allowed without permission in writing from ish.
 */

import { Amount, Contact } from '@api';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Dispatch } from 'redux';
import { getCheckoutModelFromBackend, showSyncErrors } from 'src/js/enrol/actions/Actions';
import CheckoutApiService from 'src/js/enrol/services/CheckoutApiService';
import { resetPaymentState, setPaymentFetching } from '../actions/Actions';

interface Props {
  contacts: Contact[];
  amount: Amount;
  disabled?: boolean;
  payerId?: string;
  dispatch: Dispatch<any>;
  checkoutProcessSquareCcPayment: (creditCardToken: string, verificationToken: string) => void;
}

const SquarePaymentComp = (
  {
    checkoutProcessSquareCcPayment, contacts, dispatch, amount, disabled, payerId
  }: Props) => {

  const [ready, setReady] = useState<boolean>(false);
  const [card, setCard] = useState<any>(null);
  const [payments, setPayments] = useState<any>(null);

  const init = async () => {
    try {
      const { clientKey, locationId } =  await CheckoutApiService.getClientPreferences();
      const squarePayments = (window as any).Square.payments(clientKey, locationId);
      const squareCard = await squarePayments.card();
      setCard(squareCard);
      setPayments(squarePayments);
      await squareCard.attach('#card-container');
      setReady(true);
    } catch (res) {
      console.error(res)
    }
  };

  useEffect(() => {
    init();
  }, []);

  const setLoading = (loading: boolean) => {
    dispatch(setPaymentFetching(loading));
  };

  const proceedPayment = () => {
    dispatch(resetPaymentState());
    dispatch(getCheckoutModelFromBackend());
  };

  const handleError = error => {
    dispatch(showSyncErrors({ message: error?.message }));
  };

  useEffect(() => {
    if (amount.payNow > 0) {
      proceedPayment();
    }
  }, [
    amount.payNow
  ]);

  const verifyBuyer = async token => {
    const verificationDetails = {
      amount: amount.ccPayment.toFixed(2),
      billingContact: {
        email: contacts.find(c => c.id === payerId)?.email,
      },
      currencyCode: 'AUD',
      intent: 'CHARGE',
    };

    return payments.verifyBuyer(
      token,
      verificationDetails
    );
  };

  const processSquarePayment = async () => {
    setLoading(true);
    try {
      const result = await card.tokenize();
      const verificationResults = await verifyBuyer(result.token);
      if (result.status === 'OK') {
        checkoutProcessSquareCcPayment(result.token, verificationResults.token);
      } else {
        setLoading(false);
        if (result.errors) {
          handleError({
            message: result.errors.reduce((p, c) => p + c.message + "\n", '')
          })
        }
      }
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  return (
    <div className="col text-center">
      <div id="card-container"></div>
      <button
        disabled={!ready || disabled}
        className={classnames('btn btn--primary', { disabled })}
        id="enterCardDetails"
        name="enterCardDetails"
        onClick={processSquarePayment}
      >
        Finalize checkout
      </button>
    </div>
  );
};

export default SquarePaymentComp;